/**
 * src/auth/login.js
 *
 * Email/password sign-in. Mounts a centered login card matching the
 * reference design (tokens.css .login-card family). Does NOT hardcode any
 * credentials or navigate on success — main.js's onAuthStateChanged listener
 * is the single source of truth for what's rendered (login vs shell), so a
 * successful sign-in here just lets Firebase Auth fire that listener.
 *
 * MFA-BYPASS FIX (2026-07-13): this used to call signInWithEmailAndPassword
 * DIRECTLY, which authenticates the user with zero regard for a Staffly
 * TOTP/email-OTP enrolment — any MFA-enrolled admin/hr/manager could sign in
 * on web without ever being asked for their second factor. Ported the same
 * server-enforced mint-gate bridge the Flutter app uses (see
 * lib/features/auth/login_screen.dart ~L513-688 and functions/index.ts
 * `signInWithEmailPassword`, ~L152-329):
 *
 *   1. Call the `signInWithEmailPassword` callable with { email, password }.
 *      It validates credentials server-side (Identity Toolkit REST) and only
 *      THEN consults the per-tenant MFA gate — no client can skip that step
 *      because no client ever gets a token from anywhere else.
 *   2. If the gate blocks (`failed-precondition`, message `NEEDS_MFA` or
 *      `INVALID_CODE`), reveal the 6-digit code challenge (TOTP: code field
 *      directly; email_otp: "Send code to my email" first) and re-submit the
 *      SAME callable with `mfaCode` populated.
 *   3. On an allowed mint, the callable returns a Firebase custom token —
 *      exchange it via signInWithCustomToken. auth's native code path there
 *      is separate from signInWithEmailAndPassword, so this is not just a
 *      cosmetic detour, it's the only path that actually passes through the
 *      gate.
 *
 * mfaFactorId is intentionally never sent in the request payload — the
 * Flutter reference doesn't send it either (SignInRequest.mfaFactorId exists
 * server-side for other callers; the login bridge only ever sends email/
 * password/mfaCode), so this mirrors the exact shape the CF is proven to
 * accept in production.
 */

import { signInWithCustomToken } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../lib/firebase.js';
import { mountHexBackdrop } from '../shell/hex_backdrop.js';
import { t } from '../i18n/t.js';
import { addLangListener } from '../i18n/store.js';
import { resolveErrorMessage } from '../i18n/error_codes.js';
// Theme + language toggle (B/C fix, 2026-07-21): the login card used to be
// hardcoded dark-only ("never wired to this" per the old shell.js comment).
// Reuses the SAME shell.js implementation the authenticated shell/employee
// portal already wire (wireThemeToggle/wireLangToggle/syncThemeToggle/
// syncLangToggle/defaultLogoSrc/ICON_MOON) — one implementation of each
// control, not a fork (mirrors employee_shell.js's own import of these).
import {
  defaultLogoSrc,
  ICON_MOON,
  wireThemeToggle,
  syncThemeToggle,
  wireLangToggle,
  syncLangToggle,
} from '../shell/shell.js';

/** Real Staffly logo (dark_logo.png / light_logo.png — served from
 * web-admin/public/, copied from assets/images/) rendered as plain markup
 * rather than a shared import so login.js and shell.js stay independently
 * mountable with no cross-import cycle for the DOM string itself (mirrors
 * the prior BRAND_MARK_SVG convention this replaced). THEME-AWARE (2026-07-21
 * fix, defect A): the login card now flips with the toggle like the rest of
 * the app, so the logo variant must flip with it too — defaultLogoSrc()
 * (shell.js) resolves dark_logo.png (light glyph, for the dark card) vs
 * light_logo.png (dark glyph, for the light card) from the SAME
 * html[data-theme] shell.js itself reads. The PNG already bakes in the
 * "STAFFLY" wordmark, so no adjacent text label. */
function brandLogoHtml() {
  // a11y fix (fix-wave 9, P3): was alt="" (decorative-only), which announces
  // NOTHING to screen readers on the one brand mark this pre-auth screen
  // renders — the PNG bakes in the "STAFFLY" wordmark itself (see the import
  // comment above), so it is meaningful content, not decoration.
  return `<img id="login-logo-img" src="${defaultLogoSrc()}" alt="Staffly" style="height:48px;width:auto;max-width:200px;object-fit:contain" />`;  // i18n-exempt: "Staffly" is the product name — same word in both locales.
}

// DC-MFA factor ids — mirrors lib/features/auth/mfa/mfa_service.dart
// kFactorTotp / kFactorEmailOtp (source of truth on the server + Flutter).
const FACTOR_TOTP      = 'totp';
const FACTOR_EMAIL_OTP = 'email_otp';

// Cloud Functions region: 'signInWithEmailPassword' pins REGION (us-central1,
// functions/index.ts L151) same as every other web-admin callable — no
// per-call region override needed, FUNCTIONS_REGION in lib/firebase.js
// already targets the whole `functions` instance.
//
// Timeout: signInWithEmailPassword is documented latency-critical (cross-
// region mint-gate reads, ~12s cold start) with a 30s client-side callable
// timeout on the Flutter side — mirrored here via HttpsCallableOptions. The
// 35s local hard-reset timer below is the same belt-and-braces UI-never-
// hangs guard used there (and in spirit matches guardedRead's bounded-wait
// convention in lib/api.js, which isn't reused directly here because it
// classifies `failed-precondition` as a terminal "denied" — exactly the code
// this flow needs to read as an MFA challenge, not a dead end).
const signInCallable = httpsCallable(functions, 'signInWithEmailPassword', {
  timeout: 30000,
});
// mfaChallengeEmailOtp — functions/src/auth/mfa/mfa_callables.ts L654-667.
// No-arg call; server resolves the target user + tenant from the auth
// context and emails a fresh 6-digit code. Mirrors
// lib/features/auth/mfa/mfa_service.dart:240-246 (MfaService.challengeEmailOtp).
const emailOtpChallengeCallable = httpsCallable(functions, 'mfaChallengeEmailOtp', {
  timeout: 15000,
});

/** Language-listener unsub for the CURRENT mountLogin call — mirrors
 * shell.js's `_langListenerUnsub` singleton so a sign-out/sign-in cycle
 * (mountLogin called again by main.js) never stacks a second listener on
 * the shared i18n store. */
let _loginLangUnsub = null;

export function mountLogin(root) {
  mountHexBackdrop(); // interactive hex-grid identity backdrop — idempotent, safe on repeat sign-out/in
  root.innerHTML = loginTemplate();

  // Theme + language toggles (defects C/B, 2026-07-21) — reuse the shell's
  // own implementation (see the import comment above) rather than a fork.
  // wireThemeToggle/wireLangToggle each bind their own click listener + run
  // the initial sync; refreshLoginCopy (below) is this screen's scoped
  // equivalent of shell.js's wireLangReactivity — it patches the existing
  // DOM in place on a language flip instead of a full re-render, so
  // in-progress email/password/MFA-code input is never lost.
  wireThemeToggle(root);
  root.querySelector('#theme-toggle')?.addEventListener('click', () => syncLoginLogo(root));
  wireLangToggle(root);
  if (_loginLangUnsub) _loginLangUnsub();
  _loginLangUnsub = addLangListener(() => {
    syncLangToggle(root);
    syncThemeToggle(root);
    refreshLoginCopy();
  });

  const form = root.querySelector('#login-form');
  const emailInput = root.querySelector('#login-email');
  const passwordInput = root.querySelector('#login-password');
  const errEl = root.querySelector('#login-err');
  const btn = root.querySelector('#login-btn');
  const btnLabel = root.querySelector('#login-btn-label');
  const spinner = root.querySelector('#login-spinner');

  const mfaBlock = root.querySelector('#mfa-block');
  const mfaCodeLabel = root.querySelector('#mfa-code-label');
  const mfaCodeInput = root.querySelector('#mfa-code');
  const mfaSendBtn = root.querySelector('#mfa-send-btn');
  const mfaSendBtnLabel = root.querySelector('#mfa-send-btn-label');
  const mfaSendSpinner = root.querySelector('#mfa-send-spinner');
  const mfaResendWrap = root.querySelector('#mfa-resend-wrap');
  const mfaResendLink = root.querySelector('#mfa-resend-link');

  emailInput.focus();

  /** Re-resolves every static string this screen renders through t() with
   * the NEW active language — the addLangListener subscription above calls
   * this on every flip. Scoped, in-place text patching (not a remount) so
   * whatever the caller has already typed survives the flip. renderMfaUi()
   * at the end re-derives every MFA-state-dependent label the same way; it's
   * declared below but hoisted (function declaration), so this reference is
   * safe regardless of the addLangListener callback's own invocation timing
   * (always well after mountLogin has finished running once). */
  function refreshLoginCopy() {
    const badgeEl = root.querySelector('.login-platform-badge');
    if (badgeEl) badgeEl.textContent = t('auth.portalBadge');
    const taglineEl = root.querySelector('.login-tagline');
    if (taglineEl) taglineEl.textContent = t('auth.tagline');
    const emailLabel = root.querySelector('label[for="login-email"]');
    if (emailLabel) emailLabel.textContent = t('auth.email');
    const passwordLabel = root.querySelector('label[for="login-password"]');
    if (passwordLabel) passwordLabel.textContent = t('auth.password');
    if (mfaCodeLabel) mfaCodeLabel.textContent = t('auth.verificationCode');
    if (mfaCodeInput) mfaCodeInput.placeholder = t('auth.codePlaceholder');
    // "Signing in…" only while a submit is genuinely in flight (btn.disabled
    // during that window, per the submit handler below); otherwise "Sign In".
    btnLabel.textContent = btn.disabled ? t('auth.signingIn') : t('auth.signIn');
    const noteEl = root.querySelector('#login-privacy-note');
    if (noteEl) noteEl.innerHTML = privacyNoteHtml();
    renderMfaUi();
  }

  // ── DC-MFA challenge state (module-local to this mount; reset on remount) ──
  let mfaChallengeActive = false; // gate returned NEEDS_MFA/INVALID_CODE at least once
  let mfaFactorId = null;         // 'totp' | 'email_otp' | null (unknown -> TOTP-style code field)
  let emailOtpCodeSent = false;   // email_otp: has a code been sent this challenge?
  let emailOtpSending = false;    // email_otp: send-in-flight guard

  function renderMfaUi() {
    if (!mfaChallengeActive) {
      mfaBlock.hidden = true;
      mfaCodeInput.hidden = true;
      mfaSendBtn.hidden = true;
      mfaResendWrap.hidden = true;
      return;
    }
    mfaBlock.hidden = false;

    if (mfaFactorId === FACTOR_EMAIL_OTP) {
      if (!emailOtpCodeSent) {
        // Code not yet sent — show "Send code" button, hide the code field.
        mfaSendBtn.hidden = false;
        mfaSendBtn.disabled = emailOtpSending;
        mfaSendBtnLabel.hidden = emailOtpSending;
        mfaSendBtnLabel.textContent = t('auth.sendCodeEmail');
        mfaSendSpinner.hidden = !emailOtpSending;
        mfaCodeInput.hidden = true;
        mfaCodeLabel.hidden = true;
        mfaResendWrap.hidden = true;
      } else {
        // Code sent — show the code field + resend link, hide the send button.
        mfaSendBtn.hidden = true;
        mfaCodeInput.hidden = false;
        mfaCodeLabel.hidden = false;
        mfaResendWrap.hidden = false;
        mfaResendLink.textContent = emailOtpSending ? t('auth.sendingCode') : t('auth.resendPrompt');
        mfaResendLink.style.pointerEvents = emailOtpSending ? 'none' : '';
        mfaResendLink.style.opacity = emailOtpSending ? '0.6' : '';
        mfaCodeInput.focus();
      }
    } else {
      // TOTP (or unrecognized factor — defaults to a direct code field, same
      // fallback the Flutter reference uses: login_screen.dart ~L1511-1520).
      mfaSendBtn.hidden = true;
      mfaResendWrap.hidden = true;
      mfaCodeInput.hidden = false;
      mfaCodeLabel.hidden = false;
      mfaCodeInput.focus();
    }
  }

  function resetMfaState() {
    mfaChallengeActive = false;
    mfaFactorId = null;
    emailOtpCodeSent = false;
    emailOtpSending = false;
    mfaCodeInput.value = '';
    renderMfaUi();
  }

  async function sendEmailOtpChallenge() {
    if (emailOtpSending) return;
    emailOtpSending = true;
    errEl.textContent = '';
    renderMfaUi();
    try {
      await emailOtpChallengeCallable();
      emailOtpCodeSent = true;
      emailOtpSending = false;
      renderMfaUi();
    } catch (err) {
      emailOtpSending = false;
      const code = err && err.code ? String(err.code) : '';
      errEl.textContent = code.includes('resource-exhausted')
        ? t('auth.errTooManyOtp')
        : t('auth.errOtpSendFailed');
      renderMfaUi();
    }
  }

  mfaSendBtn.addEventListener('click', sendEmailOtpChallenge);
  mfaResendLink.addEventListener('click', (e) => {
    e.preventDefault();
    sendEmailOtpChallenge();
  });

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    errEl.textContent = '';

    const email = emailInput.value.trim();
    const password = passwordInput.value;
    if (!email || !password) {
      errEl.textContent = t('auth.enterEmailPassword');
      return;
    }

    btn.disabled = true;
    btnLabel.textContent = t('auth.signingIn');
    spinner.hidden = false;

    // Hard 35s UI-reset guard — independent of the network Promise, mirrors
    // login_screen.dart's Timer(35s) belt-and-braces reset (~L500-511) so a
    // hung request never leaves the button stuck disabled.
    let hardResetTimer = setTimeout(() => {
      btn.disabled = false;
      btnLabel.textContent = t('auth.signIn');
      spinner.hidden = true;
      errEl.textContent = t('auth.signInTimeout');
    }, 35000);

    const mfaCode = mfaCodeInput.value.trim();

    try {
      const payload = { email, password };
      if (mfaCode) payload.mfaCode = mfaCode;

      const result = await signInCallable(payload);
      const data = result && result.data;
      const customToken = data && data.customToken;
      if (!customToken || typeof customToken !== 'string') {
        throw new Error(t('auth.errNoToken'));
      }

      await signInWithCustomToken(auth, customToken);
      clearTimeout(hardResetTimer);
      // main.js's onAuthStateChanged takes it from here.
      resetMfaState();
    } catch (err) {
      clearTimeout(hardResetTimer);

      const code = err && err.code ? String(err.code) : '';
      // RAW on purpose: compared against the NEEDS_MFA / INVALID_CODE control
      // sentinels below, never rendered. Routing this through the error
      // resolver would turn the sentinel into a prose sentence and silently
      // break the MFA gate.
      const gateMessage = err && err.message ? String(err.message) : '';

      if (code.includes('failed-precondition') && (gateMessage === 'NEEDS_MFA' || gateMessage === 'INVALID_CODE')) {
        // Credentials already passed — this is the MFA gate, not a
        // credential failure. Surface the second-factor challenge instead of
        // a generic error.
        const details = err && err.details;
        const factorId = details && typeof details === 'object' ? details.factorId : null;
        mfaChallengeActive = true;
        mfaFactorId = factorId || mfaFactorId;
        if (gateMessage === 'INVALID_CODE' && mfaFactorId === FACTOR_EMAIL_OTP) {
          // Code was already sent this challenge; user just entered a wrong
          // one — keep the code field showing rather than reverting to the
          // send-button state (mirrors login_screen.dart ~L650-655).
          emailOtpCodeSent = true;
        }
        errEl.textContent = gateMessage === 'INVALID_CODE'
          ? t('auth.errCodeIncorrect')
          : t('auth.enterCodeToContinue');
        renderMfaUi();
      } else {
        errEl.textContent = friendlyError(err);
      }

      btn.disabled = false;
      btnLabel.textContent = t('auth.signIn');
      spinner.hidden = true;
    }
  });
}

/**
 * Maps both signInWithEmailPassword callable failures (GIP REST codes
 * carried in err.message, per functions/index.ts L212-236) and any
 * signInWithCustomToken FirebaseAuthException to the same user-facing
 * copy the reference Flutter mapper uses (_mapBridgeError / _mapError,
 * login_screen.dart ~L696-794).
 */
function friendlyError(err) {
  const code = err && err.code ? String(err.code) : '';
  // RAW on purpose: `switch (message)` below matches GIP REST codes
  // (EMAIL_NOT_FOUND, INVALID_PASSWORD, …) that arrive AS the HttpsError
  // message. This is a control-flow match, not display text — every branch
  // returns a localized t() string. Never render `message` itself.
  const message = err && err.message ? String(err.message) : '';

  // GIP REST codes surfaced as the HttpsError message by signInWithEmailPassword.
  switch (message) {
    case 'EMAIL_NOT_FOUND':
      return t('auth.errNoAccountEmail');
    case 'INVALID_PASSWORD':
    case 'INVALID_LOGIN_CREDENTIALS':
      return t('auth.errWrongCredentials');
    case 'INVALID_EMAIL':
    case 'MISSING_PASSWORD':
    case 'MISSING_CREDENTIALS':
      return t('auth.errInvalidEmail');
    case 'USER_DISABLED':
      return t('auth.errAccountDisabled');
    case 'TOO_MANY_ATTEMPTS_TRY_LATER':
      return t('auth.errTooManyAttempts');
    case 'IDENTITY_TOOLKIT_UNREACHABLE':
      return t('auth.errAuthUnreachable');
    default:
      break;
  }

  // Direct FirebaseAuthException codes (signInWithCustomToken path) or a
  // callable code we didn't special-case above (e.g. WEB_API_KEY_NOT_CONFIGURED
  // / CUSTOM_TOKEN_MINT_FAILED / MFA_GATE_ERROR surfaced as `internal`).
  if (code.includes('wrong-password') || code.includes('invalid-credential') || code.includes('invalid-login-credentials')) {
    return t('auth.errWrongCredentials');
  }
  if (code.includes('user-not-found')) return t('auth.errNoAccountEmail');
  if (code.includes('too-many-requests') || code.includes('resource-exhausted')) return t('auth.errTooManyAttempts');
  if (code.includes('network-request-failed') || code.includes('unavailable')) return t('auth.errNetwork');
  if (code.includes('invalid-email') || code.includes('invalid-argument')) return t('auth.errInvalidEmail');
  if (code.includes('permission-denied')) return t('auth.errAccountDisabled');
  // Unmatched by every branch above -> `message` is an unrecognized raw code.
  // Rendering it would put a bare identifier on the login card, so the
  // resolver renders a localized sentence instead.
  return resolveErrorMessage(err);
}

/** Swaps the login card's logo PNG to match the CURRENT theme — called at
 * initial render (inside loginTemplate, via brandLogoHtml()) and again on
 * every theme-toggle click (see the click listener registered in
 * mountLogin) so the glyph never goes low-contrast against its own card. */
function syncLoginLogo(root) {
  const img = root.querySelector('#login-logo-img');
  if (img) img.src = defaultLogoSrc();
}

/** PDPL/privacy footer note markup (defect D, fixed fix-wave 9 P1) —
 * bilingual via auth.privacyNotice/auth.privacyPolicyLink. USED TO link to
 * `#/privacy`, the authenticated shell's Privacy/DSAR admin console
 * (screens/privacy.js) — registered only inside mountShellFrame's
 * SCREEN_MOUNTERS, i.e. gated behind login. Pre-login that hash route was
 * dead: main.js's router never starts before auth, so the tab just booted
 * back to the login screen — a PDPL compliance gap (the required privacy
 * disclosure was unreachable before sign-in). Now points at
 * `/privacy-policy.html`, a static, self-contained, pre-auth-reachable page
 * under web-admin/public/ (bilingual EN/AR, RTL-aware, theme-aware; no
 * router/auth dependency — see privacy-policy.html's own header comment).
 * ⚠️ That page's legal copy is a TEMPLATE pending owner/legal ratification
 * (visibly marked as such on the page itself) — do not treat it as final
 * until reviewed. Opening in a new tab (target=_blank) still keeps the click
 * from disrupting the in-progress sign-in form. */
function privacyNoteHtml() {
  const link = `<a href="/privacy-policy.html" target="_blank" rel="noopener" class="login-privacy-link">${t('auth.privacyPolicyLink')}</a>`;
  return t('auth.privacyNotice', { link });
}

function loginTemplate() {
  return `
    <div id="login-screen">
      <div class="login-controls">
        <button type="button" class="header-icon-btn" id="theme-toggle" aria-label="${t('common.switchToLightTheme')}">${ICON_MOON}</button>
        <button type="button" class="header-icon-btn header-lang-btn" id="lang-toggle" aria-label="${t('common.toggleLangDirection')}">AR</button>
      </div>
      <div class="login-card">
        <div class="login-brand">${brandLogoHtml()}</div>
        <div class="login-platform-badge">${t('auth.portalBadge')}</div>
        <div class="login-tagline">${t('auth.tagline')}</div>
        <form id="login-form" novalidate>
          <label class="login-label" for="login-email">${t('auth.email')}</label>
          <input class="login-input" type="email" id="login-email" autocomplete="username" autofocus required />
          <label class="login-label" for="login-password">${t('auth.password')}</label>
          <input class="login-input" type="password" id="login-password" autocomplete="current-password" required />

          <div id="mfa-block" hidden>
            <label class="login-label" for="mfa-code" id="mfa-code-label" hidden>${t('auth.verificationCode')}</label>
            <input class="login-input" type="text" id="mfa-code" inputmode="numeric" autocomplete="one-time-code" maxlength="6" placeholder="${t('auth.codePlaceholder')}" hidden />
            <button type="button" class="login-btn" id="mfa-send-btn" hidden style="margin-bottom:14px">
              <span id="mfa-send-btn-label">${t('auth.sendCodeEmail')}</span>
              <span class="login-spinner" id="mfa-send-spinner" hidden></span>
            </button>
            <div id="mfa-resend-wrap" hidden style="text-align:center;margin:-6px 0 14px;font-size:11px">
              <a href="#" id="mfa-resend-link" style="color:var(--accent);text-decoration:underline">${t('auth.resendPrompt')}</a>
            </div>
          </div>

          <button class="login-btn" id="login-btn" type="submit">
            <span id="login-btn-label">${t('auth.signIn')}</span>
            <span class="login-spinner" id="login-spinner" hidden></span>
          </button>
          <div class="login-err" id="login-err"></div>
        </form>
      </div>
      <div class="login-privacy-note" id="login-privacy-note">${privacyNoteHtml()}</div>
    </div>`;
}
